import Big from 'big.js';
import * as bootstrap from 'bootstrap';
import { DateTime } from 'luxon';
import * as hyejoo from 'smg/admin/hyejoo';
import { IapLocale } from 'smg/admin/iap-lang';
import * as session from 'smg/admin/session';
import axios from 'smg/axios';
import Batch from 'smg/batch';
import * as colorscale from 'smg/colorscale';
import * as dates from 'smg/dates';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import * as util from 'smg/util';
import i18n from '../i18n';
import { CacheKey, Orders } from './data';

interface quota {
    companyID: string;
    icon: string;
    name: string;
    targetAmount: Big;
    quotaAmount: Big;
    calculatedTargetAmount: Big;
    calculatedQuotaAmount: Big;
    targetWasSaved: boolean;
    quotaWasSaved: boolean;
    monthSum: Big;
    monthSumWithoutTwentyThousandPlus: Big;
    lastYearMonthSum: Big;
    today: Big;
}

interface row {
    company: string;
    companyName: string;
    companyID: string;
    today: Big;
    monthToDate: Big;
    quota: Big;
    target: Big;
    avgForQuota: Big;
    avgForTarget: Big;
    currentAvg: Big;
    quotaDelta: Big;
    targetDelta: Big;
}

export const Options = {
    currentIndex: 0,
    currentDesc: false,
    currentDate: DateTime.now(),
};

let defaultGoalPercentage: Big;
const quotas = new Map<string, quota[]>();

export async function MonthlyQuotas(
    parentElement: HTMLElement,
    currOrders: hyejoo.Order[],
    prevOrders: hyejoo.Order[],
    realms: Map<string, hyejoo.Realm>,
    details: Map<string, models.Detail>,
    index: number,
    desc: boolean = false,
) {
    const today = DateTime.now();
    const quota = await getQuotaAndTargetData(today, currOrders, prevOrders, realms, details);
    const table = drawMonthlyTable(today, parentElement, quota, defaultGoalPercentage, index, desc, false);

    const card = template.Clone('#monthly-quotas').firstElementChild as HTMLElement;
    (card.querySelector('.card-body') as HTMLElement).appendChild(table);

    const oldCard = parentElement.querySelector('.monthly-quota');
    if (oldCard) {
        parentElement.replaceChild(card, oldCard);
    } else {
        parentElement.appendChild(card);
    }

    parentElement.appendChild(card);

    if (await session.HasPerm('w/stats/monthlyTarget') && await session.HasPerm('w/stats/monthlyQuota')) {
        await updateTargetAndQuota(parentElement, currOrders, prevOrders, realms, details);
    }
}

function drawMonthlyTable(date: DateTime, parentElement: HTMLElement, quota: quota[], defaultGoalPercentage: Big, index: number, desc: boolean, redrawElement: boolean = false): HTMLElement {
    const table = template.Clone('#monthly-quotas-table').firstElementChild as HTMLElement;

    let todayTotal = Big(0);
    let averageForQuotaTotal: Big;
    let averageForTargetTotal: Big;
    let monthToDateTotal = Big(0);
    let quotaTotal = Big(0);
    let targetTotal = Big(0);
    let quotaDeltaTotal: Big;
    let targetDeltaTotal: Big;

    const rows: row[] = [];
    const totalWorkingDays = Big(dates.TotalWorkingDaysInMonth(date));
    const totalWorkingDaysCompletedIncludingToday = Big(dates.DaysWorking(date));
    const workingDaysLeftNotIncludingToday = Big(totalWorkingDays).sub(totalWorkingDaysCompletedIncludingToday);
    const workingDaysLeftIncludingToday = workingDaysLeftNotIncludingToday.add(1);
    const totalWorkingDaysNotIncludingToday = totalWorkingDaysCompletedIncludingToday.sub(1);
    for (const q of quota) {
        let target = Big(q.targetAmount);
        if (target.eq(0)) {
            target = Big(q.lastYearMonthSum).mul(defaultGoalPercentage);
        }

        let delta = target.sub(q.monthSum);
        if (delta.lt(0)) {
            delta = Big(0);
        }

        let avgForTarget = target.sub(q.monthSum.sub(q.today)).div(workingDaysLeftIncludingToday);
        if (avgForTarget.lte(0)) {
            avgForTarget = Big(0);
        }

        const quotaAmt = q.quotaAmount.eq(0) ? Big(q.monthSumWithoutTwentyThousandPlus).mul(1.1) : q.quotaAmount;
        let quotaDelta = Big(quotaAmt).sub(q.monthSum);
        if (quotaDelta.lt(0)) {
            quotaDelta = Big(0);
        }

        let avgForQuota = quotaAmt.sub(q.monthSum.sub(q.today)).div(workingDaysLeftIncludingToday);
        if (avgForQuota.lte(0)) {
            avgForQuota = Big(0);
        }

        const currentAvg = totalWorkingDaysNotIncludingToday.eq(0) ? Big(0) : Big(q.monthSum.sub(q.today)).div(totalWorkingDaysNotIncludingToday);

        rows.push({
            company: `<i class="${html.Escape(q.icon)} fa-fw"></i> ${html.Escape(q.name)}`,
            companyName: q.name,
            companyID: q.companyID,
            today: q.today,
            target,
            monthToDate: Big(q.monthSum),
            quota: quotaAmt,
            avgForTarget,
            avgForQuota,
            currentAvg,
            quotaDelta,
            targetDelta: delta,
        });

        todayTotal = todayTotal.add(q.today);
        monthToDateTotal = monthToDateTotal.add(q.monthSum);
        quotaTotal = quotaTotal.add(quotaAmt);
        targetTotal = targetTotal.add(target);
    }
    const currentAverageTotal = totalWorkingDaysNotIncludingToday.eq(0) ? Big(0) : Big(monthToDateTotal.sub(todayTotal)).div(totalWorkingDaysNotIncludingToday);
    averageForQuotaTotal = quotaTotal.sub(monthToDateTotal.sub(todayTotal)).div(workingDaysLeftIncludingToday);
    if (averageForQuotaTotal.lte(0)) {
        averageForQuotaTotal = Big(0);
    }

    averageForTargetTotal = targetTotal.sub(monthToDateTotal.sub(todayTotal)).div(workingDaysLeftIncludingToday);
    if (averageForTargetTotal.lte(0)) {
        averageForTargetTotal = Big(0);
    }

    quotaDeltaTotal = quotaTotal.sub(monthToDateTotal);
    if (quotaDeltaTotal.lte(0)) {
        quotaDeltaTotal = Big(0);
    }

    targetDeltaTotal = targetTotal.sub(monthToDateTotal);
    if (targetDeltaTotal.lte(0)) {
        targetDeltaTotal = Big(0);
    }

    switch (index) {
    case 0: {
        const companyEle = table.querySelector('.company') as HTMLElement;
        if (companyEle) {
            if (desc) {
                companyEle.classList.add('asc');
            } else {
                companyEle.classList.remove('asc');
            }
        }
        rows.sort((a, b) => a.companyName.localeCompare(b.companyName));
        break;
    }
    case 1: {
        const todayEle = table.querySelector('.today') as HTMLElement;
        if (todayEle) {
            if (desc) {
                todayEle.classList.add('asc');
            } else {
                todayEle.classList.remove('asc');
            }
        }
        rows.sort((a, b) => a.today.gt(b.today) ? 1 : -1);
        break;
    }
    case 2: {
        const currentAverageEle = table.querySelector('.current-average') as HTMLElement;
        if (currentAverageEle) {
            if (desc) {
                currentAverageEle.classList.add('asc');
            } else {
                currentAverageEle.classList.remove('asc');
            }
        }
        rows.sort((a, b) => a.currentAvg.gt(b.currentAvg) ? 1 : -1);
        break;
    }
    case 3: {
        const averageForQuotaEle = table.querySelector('.average-for-quota') as HTMLElement;
        if (averageForQuotaEle) {
            if (desc) {
                averageForQuotaEle.classList.add('asc');
            } else {
                averageForQuotaEle.classList.remove('asc');
            }
        }
        rows.sort((a, b) => a.avgForQuota.gt(b.avgForQuota) ? 1 : -1);
        break;
    }
    case 4: {
        const averageForTargetEle = table.querySelector('.average-for-target') as HTMLElement;
        if (averageForTargetEle) {
            if (desc) {
                averageForTargetEle.classList.add('asc');
            } else {
                averageForTargetEle.classList.remove('asc');
            }
        }
        rows.sort((a, b) => a.avgForTarget.gt(b.avgForTarget) ? 1 : -1);
        break;
    }
    case 5: {
        const monthToDateEle = table.querySelector('.month-to-date') as HTMLElement;
        if (monthToDateEle) {
            if (desc) {
                monthToDateEle.classList.add('asc');
            } else {
                monthToDateEle.classList.remove('asc');
            }
        }
        rows.sort((a, b) => a.monthToDate.gt(b.monthToDate) ? 1 : -1);
        break;
    }
    case 6: {
        const quotaEle = table.querySelector('.quota') as HTMLElement;
        if (quotaEle) {
            if (desc) {
                quotaEle.classList.add('asc');
            } else {
                quotaEle.classList.remove('asc');
            }
        }
        rows.sort((a, b) => a.quota.gt(b.quota) ? 1 : -1);
        break;
    }
    case 7: {
        const quotaDeltaEle = table.querySelector('.quota-delta') as HTMLElement;
        if (quotaDeltaEle) {
            if (desc) {
                quotaDeltaEle.classList.add('asc');
            } else {
                quotaDeltaEle.classList.remove('asc');
            }
        }
        rows.sort((a, b) => a.quotaDelta.gt(b.quotaDelta) ? 1 : -1);
        break;
    }
    case 8: {
        const targetEle = table.querySelector('.target') as HTMLElement;
        if (targetEle) {
            if (desc) {
                targetEle.classList.add('asc');
            } else {
                targetEle.classList.remove('asc');
            }
        }
        rows.sort((a, b) => a.target.gt(b.target) ? 1 : -1);
        break;
    }
    case 9: {
        const targetDeltaEle = table.querySelector('.target-delta') as HTMLElement;
        if (targetDeltaEle) {
            if (desc) {
                targetDeltaEle.classList.add('asc');
            } else {
                targetDeltaEle.classList.remove('asc');
            }
        }
        rows.sort((a, b) => a.targetDelta.gt(b.targetDelta) ? 1 : -1);
    }
    }

    if (desc) {
        rows.reverse();
    }

    let h = '';
    for (const r of rows) {
        let color;
        if (!r.quota.eq(0) && !r.target.eq(0)) {
            color = createColorScale(r.currentAvg, r.avgForQuota, r.avgForTarget);
        }
        let backgroundColor = '';
        let textColor = '';
        if (color) {
            backgroundColor = `background-color:${color.hex};`;
            textColor = `color:${color.isDark ? 'white' : 'black'};`;
        }

        let star = '';
        if (r.today.gte(r.avgForQuota)) {
            star = r.today.gte(r.avgForTarget) ? '<i class="fa fa-star animated faa-slow faa-tada"></i>' : '<i class="far fa-star"></i>';
        }

        const visible = !r.target.eq(0) || !r.quota.eq(0) || !r.monthToDate.eq(0);
        h += `
            <tr data-company-id="${r.companyID}" data-days-past="${totalWorkingDaysCompletedIncludingToday.toFixed(0)}" data-days="${totalWorkingDays.toFixed(0)}" ${!visible ? 'class="d-none"' : ''}>
                <td style="${backgroundColor}${textColor}" class="text-start">${r.company}</td>
                <td style="${backgroundColor}${textColor}" class="text-start today">${util.DollarFormat(r.today, false, true)} ${star}</td>
                <td style="${backgroundColor}${textColor}" class="text-end current-average">${util.DollarFormat(r.currentAvg, false, true)}</td>
                <td style="${backgroundColor}${textColor}" class="text-end average-for-quota">${util.DollarFormat(r.avgForQuota, false, true)}</td>
                <td style="${backgroundColor}${textColor}" class="text-end average-for-target">${util.DollarFormat(r.avgForTarget, false, true)}</td>
                <td style="${backgroundColor}${textColor}" class="text-end month-to-date" data-original="${r.monthToDate.toFixed(2)}">${util.DollarFormat(r.monthToDate, false, true)}</td>
                <td style="${backgroundColor}${textColor}" class="text-end quota">${util.DollarFormat(r.quota, false, true)}</td>
                <td style="${backgroundColor}${textColor}" class="text-end quota-delta">${util.DollarFormat(r.quotaDelta, false, true)}</td>
                <td style="${backgroundColor}${textColor}" class="text-end target">${util.DollarFormat(r.target, false, true)}</td>
                <td style="${backgroundColor}${textColor}" class="text-end target-delta">${util.DollarFormat(r.targetDelta, false, true)}</td>
            </tr>`;
    }

    const tbody = table.querySelector('tbody') as HTMLElement;
    tbody.innerHTML = h;

    const setFooter = (selector: string, value: string) => {
        const el = table.querySelector(selector) as HTMLElement | null;
        if (el) {
            el.innerHTML = value;
        }
    };

    setFooter('#today-total', util.DollarFormat(todayTotal, false, true));
    setFooter('#current-average-total', util.DollarFormat(currentAverageTotal, false, true));
    setFooter('#average-for-quota-total', util.DollarFormat(averageForQuotaTotal, false, true));
    setFooter('#average-for-target-total', util.DollarFormat(averageForTargetTotal, false, true));
    setFooter('#month-to-date-total', util.DollarFormat(monthToDateTotal, false, true));
    setFooter('#quota-total', util.DollarFormat(quotaTotal, false, true));
    setFooter('#quota-delta-total', util.DollarFormat(quotaDeltaTotal, false, true));
    setFooter('#target-total', util.DollarFormat(targetTotal, false, true));
    setFooter('#target-delta-total', util.DollarFormat(targetDeltaTotal, false, true));

    const todayTotalEle = table.querySelector('#today-total') as HTMLElement | null;
    if (todayTotalEle && todayTotal.gte(averageForQuotaTotal)) {
        todayTotalEle.innerHTML += todayTotal.gte(averageForTargetTotal) ?
            ' <i class="fa fa-star animated faa-slow faa-tada"></i>' :
            ' <i class="far fa-star"></i>';
    }

    const footers = table.querySelectorAll('tfoot td, tfoot th');
    const totalsColor = createColorScale(currentAverageTotal, averageForQuotaTotal, averageForTargetTotal);
    if (totalsColor) {
        footers.forEach(f => {
            const ele = f as HTMLElement;
            ele.style.background = totalsColor.hex;
            ele.style.color = totalsColor.isDark ? 'white' : 'black';
        });
    }

    const headers = table.querySelectorAll('thead th');
    headers.forEach((ele, i) => {
        ele.addEventListener('click', e => {
            e.preventDefault();
            Options.currentIndex = i;
            Options.currentDesc = !ele.classList.contains('asc');
            drawMonthlyTable(date, parentElement, quota, defaultGoalPercentage, Options.currentIndex, Options.currentDesc, true);
        });
    });

    if (redrawElement) {
        const t = parentElement.querySelector('.monthly-quota table') as HTMLElement | null;
        if (t) {
            t.replaceWith(table);
        }
    }

    return table;
}

async function getQuotaAndTargetData(
    date: DateTime,
    curr: hyejoo.Order[],
    prev: hyejoo.Order[],
    realms: Map<string, hyejoo.Realm>,
    details: Map<string, models.Detail>,
    ignoreCache: boolean = false,
    everyRealm: boolean = false,
): Promise<quota[]> {
    await Promise.resolve(); // ensure at least one await to satisfy lint rule
    const dateString = date.toFormat('MMyyyy');
    // The editor and the table cache separately: the editor lists every
    // realm so a goal can be set before a realm's first order.
    const cacheKey = everyRealm ? dateString + '/all' : dateString;
    if (quotas.has(cacheKey) && !ignoreCache) {
        return quotas.get(cacheKey) as quota[];
    }
    const uniqueQuotas = new Map<string, quota>();

    if (!defaultGoalPercentage) {
        defaultGoalPercentage = Big((details.get('defaultGoalPercentage')?.json || 0) as string);
        if (defaultGoalPercentage.eq(0)) {
            defaultGoalPercentage = Big(1.2);
        }
    }

    const goalAmountObject = details.get('goalAmount');
    let goalAmount: Record<string, string>[] | undefined;
    if (goalAmountObject?.json) {
        goalAmount = (goalAmountObject.json as Record<string, Record<string, string>[]>)[dateString];
    }

    const quotaAmountObject = details.get('quotaAmount');
    let quotaAmount: Record<string, string>[] | undefined;
    if (quotaAmountObject?.json) {
        quotaAmount = (quotaAmountObject.json as Record<string, Record<string, string>[]>)[dateString];
    }

    // The table's rows are the realms the orders belong to: GET /orders is
    // already limited to the orders this person may read, so grouping them by
    // realm is the scope. The realm list only supplies names and icons;
    // walking it in its own order keeps the table sorted the way the realms
    // are. The editor (everyRealm) walks all of them.
    const orderRealmIDs = new Set<string>();
    for (const o of curr) {
        if (o.companyID) {
            orderRealmIDs.add(o.companyID);
        }
    }
    for (const o of prev) {
        if (o.companyID) {
            orderRealmIDs.add(o.companyID);
        }
    }

    for (const c of realms.values()) {
        const companyID = c.id ?? '';
        if (!everyRealm && !orderRealmIDs.has(companyID)) {
            continue;
        }
        let targetAmount = Big(0);
        let quotaGoalAmount = Big(0);

        if (goalAmount) {
            const g = goalAmount.find(f => (f as Record<string, unknown>).companyID === companyID) as Record<string, string> | undefined;
            if (g) {
                targetAmount = Big(g.targetAmount);
            }
        }

        if (quotaAmount) {
            const qAmt = quotaAmount.find(f => f.companyID === companyID) as Record<string, string> | undefined;
            if (qAmt) {
                quotaGoalAmount = Big(qAmt.quotaAmount);
            }
        }

        uniqueQuotas.set(companyID, {
            companyID,
            icon: c.iconfa5 ?? '',
            name: c.name ?? '',
            targetAmount,
            quotaAmount: quotaGoalAmount,
            targetWasSaved: Boolean(goalAmount),
            quotaWasSaved: Boolean(quotaAmount),
            calculatedQuotaAmount: Big(0),
            calculatedTargetAmount: Big(0),
            monthSum: Big(0),
            monthSumWithoutTwentyThousandPlus: Big(0),
            lastYearMonthSum: Big(0),
            today: Big(0),
        });
    }

    for (const o of curr) {
        const companyID = o.companyID ?? '';
        const q = uniqueQuotas.get(companyID);
        if (!q) {
            continue;
        }
        const totalSansTax = Big(o.totalSansTax ?? 0);
        q.monthSum = q.monthSum.add(totalSansTax);
        if (o.pendingUpdated && DateTime.fromISO(o.pendingUpdated).day === date.day) {
            q.today = q.today.add(totalSansTax);
        }
        uniqueQuotas.set(companyID, q);
    }

    for (const o of prev) {
        const companyID = o.companyID ?? '';
        const q = uniqueQuotas.get(companyID);
        if (!q) {
            continue;
        }
        const totalSansTax = Big(o.totalSansTax ?? 0);
        q.lastYearMonthSum = q.lastYearMonthSum.add(totalSansTax);
        if (totalSansTax.lt(20000)) {
            q.monthSumWithoutTwentyThousandPlus = q.monthSumWithoutTwentyThousandPlus.add(totalSansTax);
        }
        uniqueQuotas.set(companyID, q);
    }

    const quotaArr = Array.from(uniqueQuotas.values());
    for (const q of quotaArr) {
        q.calculatedQuotaAmount = q.monthSumWithoutTwentyThousandPlus.mul(1.1);
        q.calculatedTargetAmount = q.lastYearMonthSum.mul(defaultGoalPercentage);
    }

    quotas.set(cacheKey, quotaArr);
    return quotas.get(cacheKey) as quota[];
}

async function updateTargetAndQuota(parentElement: HTMLElement, curr: hyejoo.Order[], prev: hyejoo.Order[], realms: Map<string, hyejoo.Realm>, details: Map<string, models.Detail>) {
    await Promise.resolve(); // ensure function contains an await
    const btn = parentElement.querySelector('#edit-monthly-quota-and-target') as HTMLButtonElement | null;
    if (!btn) {
        return;
    }
    btn.classList.remove('d-none');

    btn.addEventListener('click', e => {
        e.preventDefault();
        const tableWrapper = template.Clone('#monthly-quotas-update-table').firstElementChild as HTMLElement;
        const tableBody = tableWrapper.querySelector('tbody') as HTMLElement;

        const targetHelp = tableWrapper.querySelector('.target-header i') as HTMLElement;
        const quotaHelp = tableWrapper.querySelector('.quota-header i') as HTMLElement;

        new bootstrap.Tooltip(targetHelp, {
            title: i18n('targetsDefaultHelp'),
        });
        new bootstrap.Tooltip(quotaHelp, {
            title: i18n('quotasDefaultHelp'),
        });

        const resetFn = (ev: Event, ele: Element) => {
            ev.preventDefault();
            const input = ele.previousSibling as HTMLInputElement | null;
            if (input) {
                const original = input.getAttribute('data-original') ?? '';
                input.value = original;
            }
        };

        const drawRows = async (quotaPromise: Promise<quota[]>) => {
            const sorted = (await quotaPromise).sort((a, b) => a.name.localeCompare(b.name));

            const isOverwritten = (amt: Big, calc: Big, saved: boolean): string =>
                saved && !calc.round(2).eq(amt.round(2)) ?
                    `<i class="fa fa-strikethrough ms-2" data-calculated="${util.DollarFormat(calc, false, true)}"></i>` :
                    '';

            const goalMet = (amt: Big, goal: Big): string =>
                amt.eq(0) || goal.round(2).gt(amt.round(2)) ? '' : '<i class="fa fa-check text-success"></i>';

            tableBody.innerHTML = '';
            for (const row of sorted) {
                const quotaAmount = row.quotaAmount.eq(0) ? row.calculatedQuotaAmount : row.quotaAmount;
                const targetAmount = row.targetAmount.eq(0) ? row.calculatedTargetAmount : row.targetAmount;
                const targetInput = `
                    <td class="position-relative target-input">
                        <input data-original="${targetAmount.toFixed(2)}" value="${targetAmount.toFixed(2)}">
                        <button class="btn btn-sm position-absolute" style="left: 156px"><i class="fas fa-redo"></i></button>
                        ${isOverwritten(targetAmount, row.calculatedTargetAmount, row.targetWasSaved)}
                    </td>`;
                const quotaInput = `
                    <td class="position-relative quota-input">
                        <input data-original="${quotaAmount.toFixed(2)}" value="${quotaAmount.toFixed(2)}">
                        <button class="btn btn-sm position-absolute" style="left: 156px"><i class="fas fa-redo"></i></button>
                        ${isOverwritten(quotaAmount, row.calculatedQuotaAmount, row.quotaWasSaved)}
                    </td>`;
                const tableRow = html.FromString(`<tr data-company-id="${row.companyID}">
                    <td><i class="${row.icon} fa-fw"></i> ${row.name}</td>
                    <td>${goalMet(row.monthSum, targetAmount)}</td>
                    <td>${goalMet(row.monthSum, quotaAmount)}</td>
                    <td>${util.DollarFormat(row.lastYearMonthSum, false, true)}</td>
                    <td>${util.DollarFormat(row.monthSum, false, true)}</td>
                    ${targetInput}
                    ${quotaInput}
                </tr>`).firstElementChild as HTMLElement;

                tableBody.appendChild(tableRow);
            }

            tableBody.querySelectorAll('.target-input>button').forEach(ele => ele.addEventListener('click', ev => resetFn(ev, ele)));
            tableBody.querySelectorAll('.quota-input>button').forEach(ele => ele.addEventListener('click', ev => resetFn(ev, ele)));

            const overwritten = tableBody.querySelectorAll('.fa-strikethrough');
            overwritten.forEach(o => new bootstrap.Tooltip(o, { title: o.getAttribute('data-calculated') ?? '' }));
        };
        void drawRows(getQuotaAndTargetData(DateTime.now(), curr, prev, realms, details, false, true));

        const yearSelect = tableWrapper.querySelector('#year') as HTMLSelectElement;
        const now = DateTime.now();
        for (let i = -1; i < 5; i++) {
            const opt = document.createElement('option');
            const val = now.minus({ year: i }).toFormat('yyyy');
            opt.value = val;
            opt.innerText = val;
            if (i === 0) {
                opt.selected = true;
            }
            yearSelect.appendChild(opt);
        }

        const monthSelect = tableWrapper.querySelector('#month') as HTMLSelectElement;
        const monthLocale = IapLocale();
        for (let i = 0; i < 12; i++) {
            const opt = document.createElement('option');
            opt.value = (i + 1).toString();
            opt.innerText = DateTime.local(2000, i + 1, 1).setLocale(monthLocale).toFormat('MMMM');
            if (now.month === i + 1) {
                opt.selected = true;
            }
            monthSelect.appendChild(opt);
        }

        const parseSelectedDate = () => DateTime.local(parseInt(yearSelect.value, 10), parseInt(monthSelect.value, 10), 1, 0, 0, 0, 0);

        const spinner = tableWrapper.querySelector('#table-loading-spinner') as HTMLElement;
        const tableLoading = (loading: boolean) => {
            if (loading) {
                tableBody.style.filter = 'blur(4px)';
                spinner.classList.remove('d-none');
            } else {
                tableBody.style.filter = '';
                spinner.classList.add('d-none');
            }
        };

        // The rows on screen belong to loadedDate; a month whose load is
        // still in flight (or failed) has no rows to save, and a load that
        // finishes after a newer one started is thrown away.
        let loadedDate: DateTime | undefined = DateTime.now();
        let loadSeq = 0;
        const updateDate = async () => {
            const seq = ++loadSeq;
            loadedDate = undefined;
            tableLoading(true);
            const date = parseSelectedDate();

            try {
                const b = new Batch();
                const currOrdersPromise = Orders(date, false, b);
                const prevOrdersPromise = Orders(date.minus({ year: 1 }), false, b);

                void b.exec();

                await Promise.all([currOrdersPromise, prevOrdersPromise]);

                const currOrdersData = await (await currOrdersPromise).get(date.toFormat(CacheKey));
                const prevOrdersData = await (await prevOrdersPromise).get(date.minus({ year: 1 }).toFormat(CacheKey));

                const q = await getQuotaAndTargetData(date, currOrdersData ?? [], prevOrdersData ?? [], realms, details, false, true);
                if (seq !== loadSeq) {
                    return;
                }
                await drawRows(Promise.resolve(q));
                loadedDate = date;
            } catch (err) {
                if (seq === loadSeq) {
                    toast.Error(String(err));
                }
            } finally {
                if (seq === loadSeq) {
                    tableLoading(false);
                }
            }
        };
        yearSelect.addEventListener('change', () => void updateDate());
        monthSelect.addEventListener('change', () => void updateDate());

        new Modal({
            title: i18n('updateQuotaAndTarget'),
            body: tableWrapper,
            size: 'modal-xl',
            outsideClosable: false,
            confirmText: i18n('update'),
            onSubmit: async () => {
                const rows: HTMLTableRowElement[] = Array.from(tableBody.querySelectorAll('tbody > tr'));
                const companiesQuota: {
                    companyID: string;
                    quotaAmount: string;
                }[] = [];
                const companiesTarget: {
                    companyID: string;
                    targetAmount: string;
                }[] = [];

                for (const r of rows) {
                    const companyID = r.getAttribute('data-company-id') ?? '';
                    const target = (r.querySelector('.target-input input') as HTMLInputElement).value;
                    const quotaVal = (r.querySelector('.quota-input input') as HTMLInputElement).value;

                    companiesQuota.push({
                        companyID,
                        quotaAmount: quotaVal,
                    });
                    companiesTarget.push({
                        companyID,
                        targetAmount: target,
                    });
                }

                const date = parseSelectedDate();
                if (!loadedDate?.hasSame(date, 'month')) {
                    throw new Error(i18n('quotaMonthStillLoading'));
                }

                // Both saves settle before anything else happens: a retry
                // after one failure must not race the other save's write.
                const saves = await Promise.allSettled([
                    axios.patch('/stats/monthly-quota', {
                        companies: companiesQuota,
                        date: date.toISO(),
                    }),
                    axios.patch('/stats/monthly-target', {
                        companies: companiesTarget,
                        date: date.toISO(),
                    }),
                ]);

                // Mirror what the server stored into the details this page
                // already holds (the handlers merge by realm the same way),
                // then forget both of the month's cached views.
                const dateString = date.toFormat('MMyyyy');
                const mergeSaved = (detailName: string, amountKey: string, saved: Record<string, string>[]) => {
                    const detail = details.get(detailName);
                    if (!detail) {
                        return;
                    }
                    // A detail row whose JSON is still empty gets its first
                    // month here, as the server just did.
                    detail.json ??= {};
                    const json = detail.json as Record<string, Record<string, string>[]>;
                    const month = json[dateString] ?? [];
                    for (const row of saved) {
                        const existing = month.find(m => m.companyID === row.companyID);
                        if (existing) {
                            existing[amountKey] = row[amountKey];
                        } else {
                            month.push(row);
                        }
                    }
                    json[dateString] = month;
                };
                if (saves[0].status === 'fulfilled') {
                    mergeSaved('quotaAmount', 'quotaAmount', companiesQuota);
                }
                if (saves[1].status === 'fulfilled') {
                    mergeSaved('goalAmount', 'targetAmount', companiesTarget);
                }
                quotas.delete(dateString);
                quotas.delete(dateString + '/all');

                // Whatever was saved is shown, even when the other save
                // failed and the modal stays open with that error.
                if (date.year === DateTime.now().year && date.month === DateTime.now().month) {
                    const quotaData = await getQuotaAndTargetData(date, curr, prev, realms, details);
                    drawMonthlyTable(date, parentElement, quotaData, defaultGoalPercentage, Options.currentIndex, Options.currentDesc, true);
                }

                const failed = saves.find(s => s.status === 'rejected');
                if (failed) {
                    throw failed.reason;
                }

                toast.Success(i18n('updatedQuotaAndTarget'));
            },
        });
    });
}

// Bootstrap defaults — used when CSS vars aren't ready yet (e.g. stylesheet still loading).
const fallbackDanger = '#dc3545';
const fallbackWarning = '#ffc107';
const fallbackSuccess = '#198754';

function themeColor(cssVar: string, fallback: string): string {
    const value = getComputedStyle(document.body).getPropertyValue(cssVar).trim();
    return value || fallback;
}

function createColorScale(currentAverage: Big, quotaAverage: Big, targetAverage: Big): { hex: string; isDark: boolean } | null {
    const danger = themeColor('--bs-danger', fallbackDanger);
    const warning = themeColor('--bs-warning', fallbackWarning);
    const success = themeColor('--bs-success', fallbackSuccess);

    const smallest = quotaAverage.gt(targetAverage) ? targetAverage : quotaAverage;
    const largest = quotaAverage.gt(targetAverage) ? quotaAverage : targetAverage;

    if (currentAverage.lt(smallest)) {
        const color: Record<string, number> = { [danger]: 1 };
        return new colorscale.ColorScale(color).hexWithIsDark(1);
    }

    if (currentAverage.gt(largest)) {
        const color: Record<string, number> = { [success]: 1 };
        return new colorscale.ColorScale(color).hexWithIsDark(1);
    }

    const colors: Record<string, number> = {};
    colors[warning] = smallest.div(largest).toNumber();
    colors[success] = 1;
    return new colorscale.ColorScale(colors).hexWithIsDark(currentAverage.div(largest).toNumber());
}